import { Group, Paper, Select, SimpleGrid, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  TEAMDAILY_ZAEHLER,
  VORGANGSTYPEN,
  type TeamDailyKennzahlen,
  type TeamDailyZaehlerArt,
  type Vorgangstyp,
} from "@msc/shared";

import { TicketListeModal } from "../tickets/TicketListeModal.js";

/**
 * Die vier Zähler oben im TeamDaily (Ablösung All-KPI, Lieferung A). Jede Zahl
 * öffnet die Liste genau dieser Tickets. Ohne Team beziehen sie sich auf den
 * ganzen Bereich — das steht dabei, weil der Eingang darunter ohne Team eine
 * andere Menge zeigt (die gemeinsame Warteschlange).
 *
 * Der Vorgangstyp-Filter ist ein Select statt eines SegmentedControl: Fünf
 * Optionen (darunter „Service Request") sprengen die Breite eines Telefons.
 */
export function KennzahlenLeiste({
  daten,
  vorgangstyp,
  onVorgangstyp,
  fehler = false,
}: {
  daten: TeamDailyKennzahlen | undefined;
  vorgangstyp: Vorgangstyp | null;
  onVorgangstyp: (v: Vorgangstyp | null) => void;
  /** Die Anfrage ist gescheitert: vier „–“ und ein Hinweis statt endloser Platzhalter. */
  fehler?: boolean;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState<TeamDailyZaehlerArt | null>(null);
  const gewaehlt = daten?.zaehler.find((z) => z.art === offen) ?? null;

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" mb="xs" wrap="wrap" align="flex-end">
        <Text size="xs" c="dimmed">
          {daten
            ? t(
                daten.bezug.art === "team"
                  ? "pages.teamDaily.kennzahlen.bezugTeam"
                  : "pages.teamDaily.kennzahlen.bezugBereich",
                { count: daten.bezug.abteilungen },
              )
            : ""}
        </Text>
        <Select
          size="xs"
          aria-label={t("pages.teamDaily.kennzahlen.typLabel")}
          value={vorgangstyp ?? "alle"}
          onChange={(v) => onVorgangstyp(!v || v === "alle" ? null : (v as Vorgangstyp))}
          allowDeselect={false}
          data={[
            { value: "alle", label: t("pages.teamDaily.kennzahlen.typAlle") },
            ...VORGANGSTYPEN.map((v) => ({ value: v, label: v })),
          ]}
          style={{ minWidth: 160 }}
        />
      </Group>
      {daten?.jiraLive === false && (
        <Text size="xs" c="dimmed" mb="xs">
          {daten.jiraError ?? t("pages.teamDaily.kennzahlen.jiraNichtLive")}
        </Text>
      )}
      {fehler && !daten && (
        <Text size="xs" c="red" mb="xs">
          {t("pages.teamDaily.kennzahlen.zaehlerFehler")}
        </Text>
      )}
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
        {!daten &&
          fehler &&
          TEAMDAILY_ZAEHLER.map((art) => (
            <Paper key={art} withBorder p="xs" data-testid="zaehler-fehler">
              <Text size="xl" fw={700}>
                –
              </Text>
              <Text size="xs" c="dimmed">
                {t(`pages.teamDaily.kennzahlen.${art}`)}
              </Text>
            </Paper>
          ))}
        {!daten &&
          !fehler &&
          [0, 1, 2, 3].map((i) => <Skeleton key={i} height={62} data-testid="zaehler-platzhalter" />)}
        {(daten?.zaehler ?? []).map((z) => {
          const name = t(`pages.teamDaily.kennzahlen.${z.art}`);
          const zahl = z.anzahl === null ? "–" : `${z.gekappt ? "≥ " : ""}${z.anzahl}`;
          return (
            <UnstyledButton
              key={z.art}
              aria-label={`${name}: ${zahl}`}
              disabled={z.anzahl === null}
              onClick={() => setOffen(z.art)}
            >
              <Paper withBorder p="xs">
                <Text size="xl" fw={700}>
                  {zahl}
                </Text>
                <Text size="xs" c="dimmed">
                  {name}
                </Text>
              </Paper>
            </UnstyledButton>
          );
        })}
      </SimpleGrid>
      {gewaehlt && (
        <TicketListeModal
          opened
          onClose={() => setOffen(null)}
          titel={t(`pages.teamDaily.kennzahlen.${gewaehlt.art}`)}
          tickets={gewaehlt.tickets}
          jiraBaseUrl={daten?.jiraBaseUrl ?? null}
          spalteSeit={gewaehlt.art === "ausgesetzt"}
          spalteGeaendert={gewaehlt.art === "still"}
        />
      )}
    </Paper>
  );
}
